import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { Card, CardContent, Typography, Box } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

function EfficiencyTrend({ data, title = 'Efficiency Improvement Trend' }) {
  const chartData = useMemo(() => {
    // Group by year and calculate average efficiency
    const byYear = {}

    data.forEach(study => {
      if (!study.test_date || !study.efficiency_pct) return

      // Extract year from test_date (format: YYYY-MM-DD or similar)
      const year = new Date(study.test_date).getFullYear()
      if (isNaN(year) || year < 2000 || year > 2030) return

      if (!byYear[year]) {
        byYear[year] = {
          efficiencies: [],
          count: 0
        }
      }

      byYear[year].efficiencies.push(study.efficiency_pct)
      byYear[year].count++
    })

    // Sort years and calculate averages
    const years = Object.keys(byYear).map(Number).sort()
    const avgEfficiencies = years.map(year => {
      const data = byYear[year]
      return data.efficiencies.reduce((a, b) => a + b, 0) / data.efficiencies.length
    })
    const counts = years.map(year => byYear[year].count)

    return {
      labels: years.map(String),
      datasets: [
        {
          label: 'Avg Efficiency Improvement (%)',
          data: avgEfficiencies,
          borderColor: '#A0CE4E',
          backgroundColor: 'rgba(160, 206, 78, 0.2)',
          fill: true,
          tension: 0.3,
          pointRadius: 6,
          pointBackgroundColor: '#A0CE4E',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Studies Count',
          data: counts,
          borderColor: '#64b5f6',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.3,
          pointRadius: 4,
          pointBackgroundColor: '#64b5f6',
          yAxisID: 'y1'
        }
      ],
      years,
      avgEfficiencies
    }
  }, [data])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 0) {
              return `Avg Efficiency: ${context.raw.toFixed(1)}%`
            }
            return `Studies: ${context.raw}`
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Efficiency Improvement (%)'
        },
        min: 0,
        max: 40
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Study Count'
        },
        grid: {
          drawOnChartArea: false
        },
        min: 0
      },
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      }
    }
  }

  // Calculate overall trend
  const trendInfo = useMemo(() => {
    const { years, avgEfficiencies } = chartData
    if (years.length < 2) return null

    const firstAvg = avgEfficiencies[0]
    const lastAvg = avgEfficiencies[avgEfficiencies.length - 1]
    const change = lastAvg - firstAvg
    const direction = change >= 0 ? 'improved' : 'decreased'

    return {
      firstYear: years[0],
      lastYear: years[years.length - 1],
      change: Math.abs(change).toFixed(1),
      direction
    }
  }, [chartData])

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {trendInfo && (
            <Typography
              variant="body2"
              color={trendInfo.direction === 'improved' ? 'success.main' : 'error.main'}
              fontWeight={600}
            >
              {trendInfo.direction === 'improved' ? '+' : '-'}{trendInfo.change} pp avg since {trendInfo.firstYear}
            </Typography>
          )}
        </Box>
        <Box sx={{ height: 280 }}>
          <Line data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default EfficiencyTrend
